import React, { useEffect } from 'react';
import { marked } from 'marked';
import MultipleChoiceQuestion from '../MultipleChoiceQuestion';
import { FlagButton } from '../PartDisplay';
import { X } from 'lucide-react';

const StandardQuestionList = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleSingleAnswer,
  flaggedQuestions,
  images = [],
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet,
  onOpenMobileAnswerSheet,
  onHasAnswerSheetChange,
  onAnswerSheetOnlyNoTexts
}) => {
  const props = { flaggedQuestions, toggleFlagQuestion };

  useEffect(() => {
    if (typeof onHasAnswerSheetChange === 'function') {
      onHasAnswerSheetChange(true);
      return () => onHasAnswerSheetChange(false);
    }
  }, [onHasAnswerSheetChange]);

  const normalizeAnswer = (answer) => {
    if (typeof answer === 'boolean') {
      return answer ? 'Richtig' : 'Falsch';
    }
    return answer;
  };

  // Check if this is Goethe B1 Listening Teil 1 (short-texts)
  const isGoetheB1ListeningPart1 = partData.examProvider?.toUpperCase() === 'GOETHE' &&
                                    partData.level?.toUpperCase() === 'B1' &&
                                    partData.partNumber === 1 &&
                                    partData.taskType === 'short-texts';

  // UPDATED: Added fallback logic || partData.sectionId?.includes('listening')
  const isOsdB2ListeningPart1 = partData.examProvider?.toUpperCase() === 'OSD' &&
                                 partData.level?.toUpperCase() === 'B2' &&
                                 partData.partNumber === 1 &&
                                 (partData.taskType === 'radio-broadcast' || partData.sectionId?.includes('listening'));


  const isOsdC1ListeningPart1 = partData.examProvider?.toUpperCase() === 'OSD' &&
                                 partData.level?.toUpperCase() === 'C1' &&
                                 partData.partNumber === 1 &&
                                 (partData.taskType === 'radio-broadcast' || partData.sectionId?.includes('listening'));

  // Check if this is ÖSD B1 Reading Teil 2 (magazine layout)
  const isOsdB1Part2 = partData?.examProvider?.toUpperCase() === 'OSD' &&
                       partData?.level?.toUpperCase() === 'B1' &&
                       partData?.partNumber === 2;
  const isOsdB2Part1 = partData?.examProvider?.toUpperCase() === 'OSD' &&
                       partData?.level?.toUpperCase() === 'B2' &&
                       partData?.partNumber === 1;
  const isOsdC1Part1 = partData?.examProvider?.toUpperCase() === 'OSD' &&
                       partData?.level?.toUpperCase() === 'C1' &&
                       partData?.partNumber === 1;
  const isTelcC1Part3 = partData?.examProvider?.toUpperCase() === 'TELC' &&
                        partData?.level?.toUpperCase() === 'C1' &&
                        partData?.partNumber === 3;
  const isOsdMagazineLayout = isOsdB1Part2 || isOsdB2Part1 || isOsdC1Part1;

  const parseDocumentContent = (mainText, hasImage, isOsdMagazineLayout) => {
    if (!mainText) return { title: null, subtitle: null, content: mainText };

    const lines = mainText.split('\n').filter(line => line.trim()); // Remove empty lines

    // Special parsing for ÖSD magazine layouts (B1 Part 2 and B2 Part 1) - simpler logic
    if (isOsdMagazineLayout && lines.length >= 2) {
      const title = lines[0].replace(/\*\*/g, '').trim();
      const subtitle = lines[1].replace(/\*\*/g, '').trim();
      const content = lines.slice(2).join('\n');
      return { title, subtitle, content };
    }

    // For articles with images (Goethe B2 Part 3), extract title and subtitle
    if (hasImage && lines.length >= 2) {
      const firstLine = lines[0]?.trim();

      // Check if wrapped in ** or just plain text
      const hasMarkdown = firstLine.startsWith('**') && firstLine.endsWith('**');
      const title = hasMarkdown ? firstLine.replace(/\*\*/g, '') : firstLine;

      if (title) {
        // Smart subtitle detection
        if (lines[1]) {
          const line1 = lines[1].trim();
          const isSubtitle = line1.length > 0 &&
                            line1.length < 100 &&
                            !line1.includes('Prozent') &&
                            !line1.match(/[.!?]\s+[A-ZÄÖÜ]/);

          if (isSubtitle) {
            const subtitle = line1;
            const remainingContent = lines.slice(2).join('\n');
            return { title, subtitle, content: remainingContent };
          } else {
            const remainingContent = lines.slice(1).join('\n');
            return { title, subtitle: null, content: remainingContent };
          }
        }
      }
    }

    // Check if first line is a bold title (like **HAUSORDNUNG**)
    const firstLine = lines[0]?.trim();
    if (firstLine.startsWith('**') && firstLine.endsWith('**') && firstLine.length < 100) {
      const title = firstLine.replace(/\*\*/g, '');
      const remainingContent = lines.slice(1).join('\n');
      return { title, subtitle: null, content: remainingContent };
    }

    return { title: null, subtitle: null, content: mainText };
  };

  // --- LEFT COLUMN CONTENT (Reading) ---
  // dual-article uses content.articles (items have a .text field);
  // dual-article-comprehension uses content.texts (items have a .content field)
  const readingItems = partData.content?.texts || partData.content?.articles || [];
  const leftContent = (
    <div className="space-y-6">
      {readingItems.length > 0 ? (
        readingItems.map(text => (
          <div key={text.id} className="border-2 border-gray-900 bg-white p-6 rounded-none">
            <h3 className="text-xl font-bold text-gray-900 border-b-2 border-gray-300 pb-3 mb-4">
              {text.title}
            </h3>
            <div
              className="prose prose-sm max-w-none text-gray-900 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: marked.parse(text.content || text.text || '', { breaks: true })
              }}
            />
          </div>
        ))
      ) : (
        /* Single text: parse for document-style title */
        partData.content?.mainText || partData.content?.text || partData.content?.title ? (
         (() => {
            const hasImage = images && images.length > 0;
            const source = partData.content?.mainText || partData.content?.text || '';
            // telc C1 Teil 3 asks for the headline (item 24), so the booklet leaves it blank: drop it here too.
            const { title: docTitle, subtitle: docSubtitle, content: docContent } = isTelcC1Part3
              ? { title: null, subtitle: null, content: source.replace(/^\s*(?:#{1,6}\s+[^\n]*|\*\*(?!\d+\.?\*\*)[^\n]*\*\*)\s*\n/, '') }
              : parseDocumentContent(source, hasImage, isOsdMagazineLayout);
            return (
              <>
                {isOsdMagazineLayout ? (
                  // Magazine-style layout for ÖSD B1 Part 2 (with or without image)
                  <div className="border-2 border-gray-900 bg-white rounded-none">
                    {/* Title as prominent header/topic */}
                    <div className="py-6 px-8 bg-gray-50 border-b-2 border-gray-300">
                      {docTitle && (
                        <h1 className="text-4xl font-bold text-gray-900 mb-0 text-center">
                          {docTitle}
                        </h1>
                      )}
                    </div>

                    {/* Subtitle */}
                    {docSubtitle && (
                      <div className="py-4 px-8 border-b border-gray-200">
                        <p className="text-base text-gray-600 italic text-center">
                          {docSubtitle}
                        </p>
                      </div>
                    )}

                    {/* Content area */}
                    <div className="p-8">
                      {hasImage ? (
                        /* Layout WITH image - floating right */
                        <>
                          <div className="float-right ml-6 mb-4" style={{ width: '280px' }}>
                            {images.map((imagePath, index) => (
                              <img
                                key={index}
                                src={imagePath}
                                alt={docTitle || 'Article image'}
                                className="w-full h-auto object-cover rounded-lg shadow-md"
                              />
                            ))}
                          </div>

                          <div
                            className="prose prose-sm max-w-none text-gray-900 leading-relaxed text-justify"
                            style={{ fontSize: '14px', lineHeight: '1.6' }}
                            dangerouslySetInnerHTML={{
                              __html: marked.parse(docContent, { breaks: true })
                            }}
                          />
                          <div className="clear-both"></div>
                        </>
                      ) : (
                        /* Layout WITHOUT image - two columns */
                        <div
                          className="columns-2 gap-8 prose prose-sm max-w-none text-gray-900 leading-relaxed text-justify"
                          style={{ fontSize: '14px', lineHeight: '1.6' }}
                          dangerouslySetInnerHTML={{
                            __html: marked.parse(docContent, { breaks: true })
                          }}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  // Original layout for all other exams
                  <div className="border-2 border-gray-900 bg-white rounded-none">
                    {/* Title, Subtitle and Image Section */}
                    {hasImage && (docTitle || docSubtitle) && (
                      <>
                        <div className="text-center py-6 px-6">
                          {docTitle && (
                            <h2 className="text-3xl font-bold text-gray-900 mb-3">
                              {docTitle}
                            </h2>
                          )}
                          {docSubtitle && (
                            <p className="text-base text-gray-700">
                              {docSubtitle}
                            </p>
                          )}
                        </div>

                        {/* Image */}
                        <div className="w-full flex justify-center bg-gray-50">
                          {images.map((imagePath, index) => (
                            <img
                              key={index}
                              src={imagePath}
                              alt={`Reading context ${index + 1}`}
                              className="h-auto object-contain rounded-none"
                              style={{ maxHeight: '400px', maxWidth: '100%' }}
                            />
                          ))}
                        </div>
                      </>
                    )}

                    <div className="p-8">
                      {/* Centered Document Title (like HAUSORDNUNG) - Only show if NO image */}
                      {docTitle && !hasImage && (
                        <h2 className="text-2xl font-bold text-gray-900 text-center mb-6 pb-4 border-b-2 border-gray-300 uppercase tracking-wider">
                          {docTitle}
                        </h2>
                      )}

                      {/* Regular title if exists */}
                      {!docTitle && partData.content?.title && (
                        <h3 className="text-xl font-bold text-gray-900 border-b-2 border-gray-300 pb-3 mb-4">
                          {partData.content.title}
                        </h3>
                      )}

                      {/* Metadata */}
                      {partData.content?.metadata && (
                        <div className="text-xs text-gray-600 mb-4 pb-3 border-b border-gray-300">
                          {partData.content?.metadata.author && (
                            <span className="mr-4">Von: {partData.content.metadata.author}</span>
                          )}
                          {partData.content?.metadata.date && (
                            <span>{partData.content.metadata.date}</span>
                          )}
                        </div>
                      )}

                      {/* Main Content */}
                      {docContent && (
                        <div
                          className="prose prose-base max-w-none text-gray-900 leading-relaxed"
                          style={{
                            fontSize: '15px',
                            lineHeight: '1.7'
                          }}
                        >
                          {isTelcC1Part3 ? (
                            // Special formatting for TELC C1 Part 3 - numbered paragraphs
                            docContent.split('\n').map((paragraph, index) => {
                              const trimmedPara = paragraph.trim();
                              if (!trimmedPara) return null;

                              // Check if paragraph starts with a number
                              const match = trimmedPara.match(/^(\d+)\s+(.*)/);

                              if (match) {
                                const number = match[1];
                                const text = match[2];

                                return (
                                  <p key={index} className="mb-4 text-justify">
                                    <span className="font-bold text-xl mr-3 text-gray-900">
                                      {number}
                                    </span>
                                    <span dangerouslySetInnerHTML={{ __html: marked.parseInline(text, { breaks: true }) }} />
                                  </p>
                                );
                              }

                              return (
                                <p key={index} className="mb-4 text-justify" dangerouslySetInnerHTML={{ __html: marked.parseInline(trimmedPara, { breaks: true }) }} />
                              );
                            })
                          ) : (
                            // Original rendering for other exam types
                            <div dangerouslySetInnerHTML={{
                              __html: marked.parse(docContent, { breaks: true })
                            }} />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            );
          })()
        ) : null  /* Don't render anything if there's no content */
      )}
    </div>
  );

  // --- RIGHT COLUMN CONTENT (Answer Sheet) ---
  const answerSheet = (
    <div className="space-y-1">
      {/* Answer Sheet Header */}
      <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-4">
        <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
          Antwortbogen
        </h3>
        <p className="text-xs text-gray-600 mt-1">
          Markieren Sie Ihre Antwort.
        </p>
      </div>

      {/* --- "Beispiel" Header for Goethe B1 Listening Teil 1 --- */}
      {isGoetheB1ListeningPart1 && examples && examples.length > 0 && (
        <div className="bg-gray-100 px-4 py-2 border-t border-b border-gray-300">
          <h4 className="text-sm font-bold text-gray-900">Beispiel</h4>
        </div>
      )}

      {/* --- Examples List --- */}
      {examples && examples.map(ex => {
        const questionType = ex.questionType || partData.questionType;
        const correctAnswer = normalizeAnswer(ex.correctAnswer);

        const exampleWrapper = (content) => (
          <div
            key={ex.questionId}
            className="border border-gray-900 bg-yellow-50 p-4 rounded-none opacity-90"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <span className="font-bold text-sm mr-2">
                  {ex.questionNumber || '0'}.
                </span>
                <span className="text-sm leading-relaxed">
                  {ex.statement || ex.questionText || ex.question}
                </span>
              </div>
            </div>
            <div className="pl-6">
              {content}
            </div>
          </div>
        );

        if (questionType === 'true-false-not-in-text') {
          const options = ['+', '-', 'x'];
          const optionLabels = { '+': 'Richtig', '-': 'Falsch', 'x': 'Nicht im Text' };
          return exampleWrapper(
            <div className="grid grid-cols-3 gap-2">
              {options.map((opt) => (
                <button
                  key={opt}
                  disabled
                  className={`
                    border-2 py-2 px-2 text-xs font-bold transition-all rounded-none text-center cursor-not-allowed
                    ${correctAnswer === opt
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-400 bg-white text-gray-900'
                    }
                  `}
                >
                  {optionLabels[opt]}
                </button>
              ))}
            </div>
          );
        }

        if (questionType === 'multiple-choice' || questionType === 'true-false-not-stated' || questionType === 'multiple-choice-blank') {
          return (
            <div
              key={ex.questionId}
              className="border border-gray-900 bg-yellow-50 p-4 rounded-none opacity-90"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 flex items-start">
                  <span className="font-bold text-sm mr-2 pt-0.5">
                    {ex.questionNumber || '0'}.
                  </span>
                  <span className="text-sm leading-relaxed">
                    {ex.statement || ex.questionText || ex.question}
                  </span>
                </div>
              </div>
              <div className="space-y-2 pl-6">
                {(ex.options || []).map((opt, index) => (
                  <label
                    key={opt.id}
                    className={`
                      flex items-start p-3 border-2 rounded-none transition-all cursor-not-allowed
                      ${correctAnswer === opt.id
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-400 bg-white'
                      }
                    `}
                  >
                    <span className={`font-semibold w-5 flex-shrink-0 ${correctAnswer === opt.id ? 'text-white' : 'text-gray-900'}`}>
                      {String.fromCharCode(97 + index)}.
                    </span>
                    <span className={`flex-1 ml-3 ${correctAnswer === opt.id ? 'text-white' : 'text-gray-900'}`}>{opt.text}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        }

        if (questionType === 'true-false' || questionType === 'yes-no') {
          const options = questionType === 'true-false' ? ['Richtig', 'Falsch'] : ['Ja', 'Nein'];
          return exampleWrapper(
            <div className="grid grid-cols-2 gap-3">
              {options.map((opt) => (
                <button
                  key={opt}
                  disabled
                  className={`
                    border-2 py-2 px-4 text-sm font-bold transition-all rounded-none text-center cursor-not-allowed
                    ${correctAnswer === opt
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-400 bg-white text-gray-900'
                    }
                  `}
                >
                  {opt}
                </button>
              ))}
            </div>
          );
        }
        return null;
      })}

      {/* Questions List */}
      {(questions || []).map((q, index) => {
        const questionType = q.questionType || partData.questionType;
        const currentAnswer = partAnswers[q.questionId];

        // LOGIC UPDATE: Use loop 'index' as fallback because JSON might miss questionNumber
        let sectionNumber = null;
        let isFirstQuestionOfSection = false;
        let sectionLabel = '';

        if (isGoetheB1ListeningPart1) {
          // Goethe B1: 2 questions per text
          // Use 'index' (0-based) for safer calculation
          sectionNumber = Math.floor(index / 2) + 1;
          isFirstQuestionOfSection = index % 2 === 0;
          sectionLabel = `Text ${sectionNumber}`;

        } else if (isOsdB2ListeningPart1) {
          // ÖSD B2: Pattern 3-3-2-2 (Abschnitt 1-4)
          // Use 'index' (0-based) directly
          const questionIndex = index;
          if (questionIndex < 3) {
            sectionNumber = 1;
            isFirstQuestionOfSection = questionIndex === 0;
          } else if (questionIndex < 6) {
            sectionNumber = 2;
            isFirstQuestionOfSection = questionIndex === 3;
          } else if (questionIndex < 8) {
            sectionNumber = 3;
            isFirstQuestionOfSection = questionIndex === 6;
          } else {
            sectionNumber = 4;
            isFirstQuestionOfSection = questionIndex === 8;
          }
          sectionLabel = `Abschnitt ${sectionNumber}`;

        } else if (isOsdC1ListeningPart1) {
          // ÖSD C1: Pattern 2-3-3-2 (Abschnitt 1-4)
          // Use 'index' (0-based) directly
          const questionIndex = index;
          if (questionIndex < 2) {
            sectionNumber = 1;
            isFirstQuestionOfSection = questionIndex === 0;
          } else if (questionIndex < 5) {
            sectionNumber = 2;
            isFirstQuestionOfSection = questionIndex === 2;
          } else if (questionIndex < 8) {
            sectionNumber = 3;
            isFirstQuestionOfSection = questionIndex === 5;
          } else {
            sectionNumber = 4;
            isFirstQuestionOfSection = questionIndex === 8;
          }
          sectionLabel = `Abschnitt ${sectionNumber}`;
        }

        const questionWrapper = (content) => (
          <div
            className="border border-t-0 border-gray-900 bg-white p-4 rounded-none hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                {/* Fallback to index + 1 if questionNumber is missing */}
                <span className="font-bold text-sm mr-2">
                  {q.questionNumber || index + 1}.
                </span>
                <span className="text-sm leading-relaxed">
                  {q.statement || q.questionText || q.question}
                </span>
              </div>
              <FlagButton {...props} questionId={q.questionId} />
            </div>
            <div className="pl-6">
              {content}
            </div>
          </div>
        );

        return (
          <React.Fragment key={q.questionId}>
            {/* Add section header (Text/Abschnitt) for listening parts */}
            {(isGoetheB1ListeningPart1 || isOsdB2ListeningPart1 || isOsdC1ListeningPart1) && isFirstQuestionOfSection && (
              <div className="bg-gray-100 px-4 py-2 border-t border-b border-gray-300 mt-2">
                <h4 className="text-sm font-bold text-gray-900">{sectionLabel}</h4>
              </div>
            )}

            {/* Render the question based on type */}
            {(() => {
              if (questionType === 'true-false-not-in-text') {
                const options = ['+', '-', 'x'];
                const optionLabels = { '+': 'Richtig', '-': 'Falsch', 'x': 'Nicht im Text' };

                return questionWrapper(
                  <div className="grid grid-cols-3 gap-2">
                    {options.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => handleSingleAnswer(q.questionId, opt)}
                        className={`
                          border-2 py-2 px-2 text-xs font-bold transition-all rounded-none text-center
                          ${currentAnswer === opt
                            ? 'border-gray-900 bg-gray-900 text-white'
                            : 'border-gray-400 bg-white text-gray-900 hover:border-gray-900'
                          }
                        `}
                      >
                        {optionLabels[opt]}
                      </button>
                    ))}
                  </div>
                );
              }

              if (questionType === 'multiple-choice' || questionType === 'true-false-not-stated' || questionType === 'multiple-choice-blank') {
                return questionWrapper(
                  <MultipleChoiceQuestion
                    question={q}
                    onAnswer={handleSingleAnswer}
                    savedAnswer={currentAnswer}
                  />
                );
              }

              if (questionType === 'true-false' || questionType === 'yes-no') {
                const options = questionType === 'true-false' ? ['Richtig', 'Falsch'] : ['Ja', 'Nein'];

                return questionWrapper(
                  <div className="grid grid-cols-2 gap-3">
                    {options.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => handleSingleAnswer(q.questionId, opt)}
                        className={`
                          border-2 py-2 px-4 text-sm font-bold transition-all rounded-none text-center
                          ${currentAnswer === opt
                            ? 'border-gray-900 bg-gray-900 text-white'
                            : 'border-gray-400 bg-white text-gray-900 hover:border-gray-900'
                          }
                        `}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                );
              }

              return null;
            })()}
          </React.Fragment>
        );
      })}
    </div>
  );

  // --- MAIN LAYOUT ---
  const isAudioTask = partData.taskType?.includes('short-texts') ||
                    partData.taskType?.includes('conversation') ||
                    partData.taskType?.includes('discussion') ||
                    partData.taskType?.includes('monologue') ||
                    partData.taskType?.includes('lecture') ||
                    partData.taskType?.includes('radio') ||
                    partData.taskType?.includes('broadcast') ||
                    partData.taskType?.includes('radio-interview') ||
                    partData.taskType?.includes('mixed-comprehension');

  // Detect "answer sheet only" (no texts to show) for listening – so parent can auto-open answer sheet on mobile
  const hasNoLeftContent = !(partData.content?.texts?.length) &&
    !(partData.content?.mainText || partData.content?.text || partData.content?.title);
  const answerSheetOnlyNoTexts = Boolean(isAudioTask && hasNoLeftContent);

  useEffect(() => {
    if (typeof onAnswerSheetOnlyNoTexts !== 'function') return;
    onAnswerSheetOnlyNoTexts(answerSheetOnlyNoTexts);
    return () => onAnswerSheetOnlyNoTexts(false);
  }, [answerSheetOnlyNoTexts, onAnswerSheetOnlyNoTexts]);

  const mobileAnswerSheetOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
          <button
            type="button"
            onClick={onCloseMobileAnswerSheet}
            className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center"
            aria-label="Schließen"
          >
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain p-4 pb-safe">
          {answerSheet}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
      {isAudioTask ? (
        // Single column layout for audio/listening tasks - answer sheet below on desktop; on mobile hidden and shown in overlay
        <div className="max-w-4xl mx-auto">
          {leftContent}
          <div className="mt-6 hidden md:block">
            {answerSheet}
          </div>
        </div>
      ) : (
        // Two column layout for reading tasks - answer sheet column hidden on mobile (shown in overlay)
        <div className="grid grid-cols-1 md:grid-cols-5 md:gap-8">
          {/* Left Column (Reading Content) */}
          <div className="md:col-span-3 min-w-0">
            {leftContent}
            {onOpenMobileAnswerSheet && (
              <button
                type="button"
                onClick={onOpenMobileAnswerSheet}
                className="md:hidden w-full mt-4 py-3 px-4 bg-[#006D77] text-white font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform touch-manipulation"
              >
                <span>Fragen beantworten</span>
                <span aria-hidden>→</span>
              </button>
            )}
          </div>

          {/* Right Column (Answer Sheet) - tablet and up; mobile uses overlay */}
          <div className="hidden md:block md:col-span-2 mt-6 md:mt-0">
            <div className="md:sticky md:top-6">
              {answerSheet}
            </div>
          </div>
        </div>
      )}
      {mobileAnswerSheetOverlay}
    </div>
  );
};

export default StandardQuestionList;